/**
 * Translation Hook for WoodAlps Multilingual Support
 * 
 * Provides translation functionality for hardcoded strings with:
 * - Automatic locale detection from Next.js router
 * - Fallback to English when translations are missing
 * - Dynamic translation file loading
 * - Nested object access for organized translations
 * - Error handling for missing keys
 * 
 * Usage:
 * const t = useTranslation();
 * const text = t('navigation.contact_us'); // Returns translated text
 * 
 * @description Lightweight translation hook with fallback support
 */

import { useRouter } from 'next/router';
import { useState, useEffect } from 'react';

// Cache for loaded translations to avoid repeated file loading
const translationCache = {};

// Flag to track if initial load has completed
let initialLoadComplete = false;

export const useTranslation = () => {
  const router = useRouter();
  const [translations, setTranslations] = useState({});
  const [isLoading, setIsLoading] = useState(!initialLoadComplete);
  
  // Get current locale from Next.js router, fallback to 'en'
  const locale = router.locale || 'en';

  // Load translation files when locale changes
  useEffect(() => {
    const loadTranslations = async () => {
      // Only show loading state if translations are not cached
      if (!translationCache[locale]) {
        setIsLoading(true);
      }
      
      try {
        // Check if translations are already cached
        if (translationCache[locale]) {
          setTranslations(translationCache[locale]);
          setIsLoading(false);
          initialLoadComplete = true;
          return;
        }

        // Load translation file for current locale
        const translationModule = await import(`../../locales/${locale}.json`);
        const translations = translationModule.default;
        
        // Cache the translations
        translationCache[locale] = translations;
        setTranslations(translations);
        initialLoadComplete = true;
        
      } catch (error) {
        console.warn(`Failed to load translations for locale: ${locale}`, error);
        
        // Fallback to English if locale file fails to load
        if (locale !== 'en') {
          try {
            const fallbackModule = await import(`../../locales/en.json`);
            const fallbackTranslations = fallbackModule.default;
            translationCache[locale] = fallbackTranslations;
            setTranslations(fallbackTranslations);
            initialLoadComplete = true;
          } catch (fallbackError) {
            console.error('Failed to load fallback English translations', fallbackError);
            setTranslations({});
          }
        } else {
          setTranslations({});
        }
      } finally {
        setIsLoading(false);
      }
    };

    loadTranslations();
  }, [locale]);

  // Translation function with nested object support
  const t = (key, fallback = null) => {
    // If still loading and no translations cached, return empty string to prevent flash
    if (isLoading && Object.keys(translations).length === 0) {
      return fallback || '';
    }

    // Split key by dots to access nested objects (e.g., 'navigation.contact_us')
    const keys = key.split('.');
    let value = translations;

    // Traverse the nested object structure
    for (const k of keys) {
      if (value && typeof value === 'object' && k in value) {
        value = value[k];
      } else {
        value = undefined;
        break;
      }
    }

    // If translation exists and is not empty, return it
    if (value && typeof value === 'string' && value.trim() !== '') {
      return value;
    }

    // Fallback logic
    if (locale !== 'en' && translationCache['en']) {
      // Try to get English fallback
      let englishValue = translationCache['en'];
      for (const k of keys) {
        if (englishValue && typeof englishValue === 'object' && k in englishValue) {
          englishValue = englishValue[k];
        } else {
          englishValue = undefined;
          break;
        }
      }
      
      if (englishValue && typeof englishValue === 'string' && englishValue.trim() !== '') {
        return englishValue;
      }
    }

    // Final fallback: return provided fallback or the key itself
    return fallback || key;
  };

  // Return translation function and utility info
  return {
    t,
    locale,
    isLoading,
    // Helper function to check if a translation exists
    hasTranslation: (key) => {
      const keys = key.split('.');
      let value = translations;
      for (const k of keys) {
        if (value && typeof value === 'object' && k in value) {
          value = value[k];
        } else {
          return false;
        }
      }
      return value && typeof value === 'string' && value.trim() !== '';
    }
  };
};

// Export a simpler version that just returns the translation function
export const useTranslationFunction = () => {
  const { t } = useTranslation();
  return t;
};

export default useTranslation;
